<template>
  <li class="file-list-item" :data-valid="isFileValid" :data-failed="file.state === 'failed'">
    <!-- Primary row -->
    <div class="file-item-primary">
      <!-- Status column -->
      <div class="file-status">
        <!-- Success -->
        <div v-if="file.state === 'success'">
          <IconSuccess width="18" height="18" />
        </div>

        <!-- In progress -->
        <small v-else-if="file.state === 'uploading'" class="muted" style="translate: 0 4px;">
          {{ file.progress }}%
        </small>

        <!-- Invalid -->
        <small v-else-if="!isFileValid" :style="{ color: '#e10000' }">
          <IconStrikedCircle width="18" height="18" />
        </small>

        <!-- Failed -->
        <small v-else-if="file.state === 'failed'" :style="{ color: '#e10000' }">
          <IconCrossedCircle width="18" height="18" />
        </small>
      </div>

      <!-- Main column -->
      <div class="main-column file-meta muted-on-fail">
        <!-- File name -->
        <span :title="file.file.name" class="truncate">
          {{ file.file.name }}
        </span>

        <!-- File-size -->
        <small class="muted">
          {{ getFileSizeLabel(file.file.size) }}
        </small>
      </div>
    </div>

    <!-- Secondary row -->
    <div v-if="!isFileValid || file.state === 'failed'" class="file-item-secondary">
      <div class="main-column">
        <span v-for="(error, index) in errors" :key="index" :style="{ color: '#e10000' }">
          {{ error }}.
        </span>

        <span v-if="file.state === 'failed'" :style="{ color: '#e10000' }">
          {{ t('fileupload.fileUploadErrorMessage') }}.
        </span>
      </div>
    </div>

    <!-- Action buttons -->
    <div class="full-column-action">
      <div class="file-action" :style="[file.state !== 'failed' && 'height: 100%;']">
        <button
          v-if="file.state === 'pending' || file.state === 'failed'"
          :aria-label="t('fileupload.buttonLabel.remove')"
          :title="t('fileupload.buttonLabel.remove')"
          @click="emit('removeFile', file.uuid)"
        >
          <IconDelete width="18" height="18" fill="black" />
        </button>
      </div>

      <div v-if="file.state === 'failed'" class="file-action">
        <button
          :aria-label="t('fileupload.buttonLabel.tryAgain')"
          :title="t('fileupload.buttonLabel.tryAgain')"
          @click="emit('retryFile', file.uuid)"
        >
          <IconReload width="18" height="18" fill="black" />
        </button>
      </div>
    </div>

    <progress
      v-if="file.state === 'uploading'"
      class="progressBar"
      max="100"
      :value="file.progress"
      :class="file.state"
    />
  </li>
</template>

<script setup lang="ts">
import type { FileOptions, FileToUpload } from './useUploadManager';
import { getFileSizeLabel, validateFile, VALIDATIONERRORS } from './helper';
import { computed, toRef } from 'vue';
import { useI18n } from '@/composables/useI18n';
import IconStrikedCircle from '@/svg/IconStrikedCircle.vue';
import IconCrossedCircle from '@/svg/IconCrossedCircle.vue';
import IconSuccess from '@/svg/IconSuccess.vue';
import IconDelete from '@/svg/IconDelete.vue';
import IconReload from '@/svg/IconReload.vue';

const { t } = useI18n();

const props = defineProps<{
  file: FileToUpload,
  options: FileOptions
}>();

const file = toRef(props.file);
const isFileValid = computed(() => {
  return errors.value.length === 0
});

const errors = computed(() => {
  const valerrors = validateFile(file.value.file, props.options);

  const errorTexts = valerrors.map((error) => {
    if (error === VALIDATIONERRORS.FILESIZE) {
      return t('fileupload.filesizeErrorMessage')
    }
    if (error === VALIDATIONERRORS.FILETYPE) {
      return t('fileupload.filetypeErrorMessage')
    }
  });

  return errorTexts;
});

const emit = defineEmits<{
  removeFile: [id: string];
  retryFile: [id: string];
}>()
</script>

<style scoped lang="scss">
/* Main container for item */
.file-list-item {
  position: relative;
  background: #F5F5F5;
  border-radius: $kw-widget-global-borderradius-inner;
  overflow: hidden;

  display: grid;
  grid-template-columns: [status-start] minmax(auto, 36px) [status-end main-start] 1fr [main-end actions-start] auto [actions-end];
  grid-template-rows: [primary-start] minmax(30px, auto) [primary-end secondary-start] auto [secondary-end];

  /* Establish a baseline font size for the file */
  font-size: 14px;
  line-height: 1.2;
  /* Needs enough line-height to show full descenders */
}

/* Main row that holds status icon, file meta, and main action (delete) */
.file-item-primary {
  display: grid;
  grid-template-columns: subgrid;
  grid-template-rows: subgrid;
  grid-column: status-start / main-end;

  .main-column {
    padding-top: 4px;
    margin-right: 6px;
  }
}

/* Add equal padding on bottom if there's no secondary row */
.file-list-item:not(:has(.file-item-secondary)) .main-column {
  padding-bottom: 4px;
  padding-top: 6px;
}

/* Holds errors and other messages */
.file-item-secondary {
  display: grid;
  grid-template-columns: subgrid;
  grid-template-rows: subgrid;
  grid-column: status-start / main-end;

  .main-column {
    padding-bottom: 4px;
    margin-right: 6px;

    /* Consistent height with upload failed vs. invalid file, due to icon's height */
    min-height: 28px;
  }
}

/* Designate a main (middle) column */
.main-column {
  grid-column: main;
}

/* Contains file name & size */
.file-meta {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 6px;

  /* For text truncate */
  overflow: hidden;

  small {
    /* Keep file-size from wrapping */
    flex-shrink: 0;
  }
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A status container */
.file-status {
  padding: 4px;
  display: flex;
  justify-content: center;
}

.file-action button:focus {
  outline: solid 2px black;
  outline-offset: -2px;
  /*
  Ideally we round right-side when button is on right side, and left side when button is on left.
  But we first need to decide how to deal with failure retries, maybe all buttons will only be on right side.
   */
  border-radius: $kw-widget-global-borderradius-inner;
}

.full-column-action {
  grid-row: primary-start / secondary-end;
  grid-column: actions;

  /* Ensure buttons fills height in case error spans multiple lines */
  display: flex;
  flex-direction: column;

  >div {
    flex-grow: 1;
  }
}

/* Low-light color */
.muted,
[data-failed="true"] .muted-on-fail,
[data-valid="false"] .muted-on-fail {
  color: #6f6f6f;
}

button {
  padding: 4px;

  cursor: pointer;
  border: none;
  background: none;
  height: 100%;

  /* For when button heights grow in height due to multi-line errors */
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus,
  &:hover {
    /* This is the color from the link/QR button's border */
    background: #E0E0E0;
  }
}

.progressBar {
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 2px;

  -webkit-appearance: none;
  background: transparent;
  border: none;
}

::-moz-progress-bar,
::-webkit-progress-bar {
  background-color: black;
}
</style>
